/**
 * PrivacyPolicy — Sprint D (Feb 2026). 16 sections, verbatim from legal@.
 * Last Updated: July 17, 2026.
 */
import { ShieldCheck } from 'lucide-react';
import {
  LegalPageShell, HeroCard, LegalLanguageBar, Section, P, Bullet, SubHeading,
  ContactBox, EmailLink, ExternalLink, LegalFooter,
} from '@/components/legal/LegalPrimitives';

export default function PrivacyPolicy() {
  return (
    <LegalPageShell title="Privacy Policy">
      <HeroCard icon={ShieldCheck} title="Meet to Keep Privacy Policy" lastUpdated="July 17, 2026" />
      <LegalLanguageBar docKey="privacy" />

      <P>Meet to Keep (&ldquo;Meet to Keep,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) values your privacy. This Privacy Policy explains how we collect, use, disclose, store, and protect your personal information when you use our website, mobile applications, and related services (collectively, the &ldquo;Services&rdquo;).</P>
      <P>By creating an account or using Meet to Keep, you acknowledge that you have read and understand this Privacy Policy.</P>

      <Section number={1} title="Eligibility">
        <P>Meet to Keep is intended only for individuals who are 18 years of age or older.</P>
        <P>We do not knowingly collect personal information from anyone under 18. If we discover that an account belongs to someone under 18, we may remove the account and delete associated information.</P>
      </Section>

      <Section number={2} title="Information We Collect">
        <P>Depending on how you use Meet to Keep, we may collect:</P>

        <SubHeading>Account Information</SubHeading>
        <Bullet>Name</Bullet>
        <Bullet>Username</Bullet>
        <Bullet>Email address</Bullet>
        <Bullet>Phone number</Bullet>
        <Bullet>Date of birth</Bullet>
        <Bullet>Gender</Bullet>
        <Bullet>Relationship preferences</Bullet>
        <Bullet>Password (encrypted)</Bullet>

        <SubHeading>Profile Information</SubHeading>
        <Bullet>Profile photos</Bullet>
        <Bullet>Biography</Bullet>
        <Bullet>Interests</Bullet>
        <Bullet>Compatibility questionnaire responses</Bullet>
        <Bullet>Lifestyle preferences</Bullet>
        <Bullet>Education</Bullet>
        <Bullet>Occupation</Bullet>
        <Bullet>Height</Bullet>
        <Bullet>Languages</Bullet>
        <Bullet>Religious preferences (if voluntarily provided)</Bullet>
        <Bullet>Political preferences (if voluntarily provided)</Bullet>
        <Bullet>Other information you choose to display</Bullet>

        <SubHeading>Communications</SubHeading>
        <Bullet>Messages exchanged through the app</Bullet>
        <Bullet>AI Icebreaker requests</Bullet>
        <Bullet>Customer support conversations</Bullet>
        <Bullet>Reports submitted about other users</Bullet>

        <SubHeading>Device Information</SubHeading>
        <Bullet>Device type</Bullet>
        <Bullet>Operating system</Bullet>
        <Bullet>Browser type</Bullet>
        <Bullet>IP address</Bullet>
        <Bullet>Device identifiers</Bullet>
        <Bullet>Crash reports</Bullet>
        <Bullet>App version</Bullet>

        <SubHeading>Usage Information</SubHeading>
        <P>We collect information about how you use Meet to Keep, including:</P>
        <Bullet>Likes</Bullet>
        <Bullet>Swipes</Bullet>
        <Bullet>Matches</Bullet>
        <Bullet>Profile views</Bullet>
        <Bullet>Time spent in the app</Bullet>
        <Bullet>Feature usage</Bullet>
        <Bullet>Search activity</Bullet>
        <Bullet>Login history</Bullet>

        <SubHeading>Location Information</SubHeading>
        <P>If you grant permission, we collect:</P>
        <Bullet>GPS location</Bullet>
        <Bullet>Approximate location</Bullet>
        <Bullet>City</Bullet>
        <Bullet>Country</Bullet>
        <P>Your exact GPS location is never displayed to other users.</P>
        <P>Only general location information, such as your city or distance, may appear on your profile.</P>
        <P><strong>Voice and video calling.</strong> When you use Meet to Keep&rsquo;s in-app voice or video calling features with a matched user, we collect and store call metadata for service functionality, safety, abuse prevention, troubleshooting, and operational analytics:</P>
        <Bullet>The type of call (voice or video)</Bullet>
        <Bullet>The match the call was placed within</Bullet>
        <Bullet>The participants (Meet to Keep user identifiers only &mdash; not telephone numbers)</Bullet>
        <Bullet>Timestamps for invite, accept, decline, hangup, and missed events</Bullet>
        <Bullet>The outcome of each call session (completed, missed, declined, canceled)</Bullet>
        <Bullet>Total call duration in minutes, used to enforce subscription-tier limits and to bill infrastructure usage</Bullet>
        <Bullet>Technical identifiers required by our communications provider to route the session (for example, a session or room identifier scoped to the match)</Bullet>
        <P><strong>We do not record or store the audio or video content of your calls.</strong> Call media is transmitted peer-to-peer through our third-party communications provider and is not captured on our servers. Your telephone number is never exposed to the other user through calling features.</P>
        <P>To establish and operate calls, we transmit the call-session identifier, your user identifier, and the match identifier to our communications provider. That provider processes the real-time media stream between you and the other user; we do not receive a copy of the media. Call metadata described above is retained for as long as your account is active (subject to the retention terms elsewhere in this Policy) so we can provide call history and enforce subscription limits.</P>
      </Section>

      <Section number={3} title="How We Use Your Information">
        <P>We use your information to:</P>
        <Bullet>Create and manage your account</Bullet>
        <Bullet>Match you with compatible users</Bullet>
        <Bullet>Calculate compatibility scores</Bullet>
        <Bullet>Generate AI conversation starters</Bullet>
        <Bullet>Display nearby matches</Bullet>
        <Bullet>Improve our matching algorithms</Bullet>
        <Bullet>Personalize your experience</Bullet>
        <Bullet>Verify accounts</Bullet>
        <Bullet>Detect spam, scams, bots, and fake profiles</Bullet>
        <Bullet>Investigate reports of abuse</Bullet>
        <Bullet>Moderate content</Bullet>
        <Bullet>Prevent fraud</Bullet>
        <Bullet>Respond to customer support requests</Bullet>
        <Bullet>Enforce our Terms of Service</Bullet>
        <Bullet>Improve our products and services</Bullet>
        <Bullet>Comply with legal obligations</Bullet>
        <Bullet>Route and connect your in-app voice and video calls, enforce your monthly calling limits, and investigate reports of calling misuse</Bullet>
      </Section>

      <Section number={4} title="Artificial Intelligence Features">
        <P>Meet to Keep may use artificial intelligence to provide features such as:</P>
        <Bullet>Conversation starters</Bullet>
        <Bullet>Compatibility insights</Bullet>
        <Bullet>Profile recommendations</Bullet>
        <Bullet>Safety analysis</Bullet>
        <Bullet>Content moderation</Bullet>
        <Bullet>Scam detection</Bullet>
        <P>Information submitted to AI features may be processed by trusted AI service providers acting on our behalf.</P>
        <P>We do not use AI to make legally significant decisions without appropriate human oversight where required by law.</P>
      </Section>

      <Section number={5} title="When We Share Information">
        <P bold className="!text-[#0F0F10]"><b style={{ color: '#0F0F10', fontWeight: 800 }}>We do not sell your personal information.</b></P>
        <P>We may share information with:</P>

        <SubHeading>Service Providers</SubHeading>
        <P>Companies that help us operate Meet to Keep, including providers of:</P>
        <Bullet>Cloud hosting</Bullet>
        <Bullet>Authentication</Bullet>
        <Bullet>Customer support</Bullet>
        <Bullet>Analytics</Bullet>
        <Bullet>Payment processing</Bullet>
        <Bullet>Push notifications</Bullet>
        <Bullet>Email delivery</Bullet>
        <Bullet>AI services</Bullet>
        <Bullet>Voice and video communications infrastructure (for routing in-app calls and delivering call notifications)</Bullet>
        <Bullet>Fraud prevention</Bullet>
        <P>These providers may access information only as necessary to perform services for us.</P>

        <SubHeading>Other Users</SubHeading>
        <P>Other users may see information that you choose to make public, including:</P>
        <Bullet>Photos</Bullet>
        <Bullet>First name or display name</Bullet>
        <Bullet>Age</Bullet>
        <Bullet>City</Bullet>
        <Bullet>Bio</Bullet>
        <Bullet>Interests</Bullet>
        <Bullet>Compatibility information you choose to share</Bullet>

        <SubHeading>Legal Requirements</SubHeading>
        <P>We may disclose information when required to:</P>
        <Bullet>Comply with applicable law</Bullet>
        <Bullet>Respond to lawful requests from government authorities</Bullet>
        <Bullet>Comply with subpoenas or court orders</Bullet>
        <Bullet>Investigate illegal activity</Bullet>
        <Bullet>Protect the safety of users</Bullet>
        <Bullet>Protect our legal rights</Bullet>

        <SubHeading>Business Transfers</SubHeading>
        <P>If Meet to Keep is involved in a merger, acquisition, investment, restructuring, or sale of assets, user information may be transferred as part of that transaction.</P>
      </Section>

      <Section number={6} title="Cookies and Similar Technologies">
        <P>We use cookies and similar technologies to:</P>
        <Bullet>Authenticate users</Bullet>
        <Bullet>Maintain login sessions</Bullet>
        <Bullet>Improve performance</Bullet>
        <Bullet>Remember preferences</Bullet>
        <Bullet>Measure usage</Bullet>
        <Bullet>Detect fraud</Bullet>
        <P>We may also use analytics tools to better understand how users interact with the Services.</P>
      </Section>

      <Section number={7} title="Data Security">
        <P>We use commercially reasonable administrative, technical, and organizational safeguards designed to protect your information.</P>
        <P>These safeguards may include:</P>
        <Bullet>Encryption in transit</Bullet>
        <Bullet>Encryption at rest</Bullet>
        <Bullet>Secure cloud storage</Bullet>
        <Bullet>Access controls</Bullet>
        <Bullet>Authentication systems</Bullet>
        <Bullet>Security monitoring</Bullet>
        <P>However, no method of electronic storage or transmission is completely secure, and we cannot guarantee absolute security.</P>
      </Section>

      <Section number={8} title="Data Retention">
        <P>We retain your information while your account remains active.</P>
        <P>After account deletion, we may retain certain information for a limited period when necessary to:</P>
        <Bullet>Comply with legal obligations</Bullet>
        <Bullet>Resolve disputes</Bullet>
        <Bullet>Prevent fraud</Bullet>
        <Bullet>Enforce our agreements</Bullet>
        <Bullet>Protect users</Bullet>
        <Bullet>Maintain security</Bullet>
      </Section>

      <Section number={9} title="Your Rights">
        <P>Depending on your location, you may have the right to:</P>
        <Bullet>Access your information</Bullet>
        <Bullet>Correct inaccurate information</Bullet>
        <Bullet>Delete your account</Bullet>
        <Bullet>Request a copy of your information</Bullet>
        <Bullet>Object to certain processing</Bullet>
        <Bullet>Withdraw consent where applicable</Bullet>
        <P>You may also:</P>
        <Bullet>Update your profile</Bullet>
        <Bullet>Remove photos</Bullet>
        <Bullet>Block users</Bullet>
        <Bullet>Report users</Bullet>
        <Bullet>Delete your account through Settings</Bullet>
      </Section>

      <Section number={10} title="California Privacy Rights">
        <P>If you are a California resident, you may have additional rights under applicable California privacy laws, including the right to know, access, correct, delete, and opt out of certain data-sharing practices, subject to applicable exceptions.</P>
        <P>Meet to Keep does not sell personal information as that term is defined under applicable California law.</P>
      </Section>

      <Section number={11} title="International Users">
        <P>Your information may be processed and stored in the United States or other countries where we or our service providers operate.</P>
        <P>By using Meet to Keep, you acknowledge that your information may be transferred to jurisdictions with different data protection laws than your own.</P>
      </Section>

      <Section number={12} title="Third-Party Links">
        <P>Our Services may contain links to third-party websites or services.</P>
        <P>We are not responsible for their privacy practices or content.</P>
      </Section>

      <Section number={13} title="User Safety">
        <P>Meet to Keep encourages users to exercise caution when meeting people in person.</P>
        <P>Always:</P>
        <Bullet>Meet in public places.</Bullet>
        <Bullet>Tell a trusted friend where you are going.</Bullet>
        <Bullet>Arrange your own transportation.</Bullet>
        <Bullet>Avoid sending money to someone you have only met online.</Bullet>
        <Bullet>Report suspicious behavior through the app.</Bullet>
        <P>While we work to reduce fraud and harmful conduct, we cannot guarantee the identity, intentions, or actions of any user.</P>
      </Section>

      <Section number={14} title="Account Deletion">
        <P>You may delete your account at any time through the Settings menu.</P>
        <P>Deleting your account removes your profile from public view and initiates deletion of your personal information, subject to legal, security, fraud-prevention, and backup retention requirements.</P>
      </Section>

      <Section number={15} title="Changes to This Privacy Policy">
        <P>We may update this Privacy Policy periodically.</P>
        <P>If we make material changes, we will update the &ldquo;Last Updated&rdquo; date and may notify users through the app or by email where appropriate.</P>
        <P>Your continued use of Meet to Keep after changes become effective constitutes acceptance of the updated Privacy Policy.</P>
      </Section>

      <Section number={16} title="Contact Us">
        <P>If you have questions about this Privacy Policy or your personal information, contact us:</P>
        <ContactBox>
          <p className="text-[13.5px] font-[700]" style={{ color: '#0F0F10' }}>Meet to Keep</p>
          <EmailLink label="Email" address="privacy@meettokeep.com" />
          <EmailLink label="Support" address="support@meettokeep.com" />
          <ExternalLink label="Website" href="https://meettokeep.com" />
        </ContactBox>
      </Section>

      <LegalFooter />
    </LegalPageShell>
  );
}
